import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, MapPin, Package, RefreshCw, User } from "lucide-react";
import { toast } from "sonner";
import { Reveal, EASE } from "../components/Reveal";
import { Sprig } from "../components/ProductVisual";
import { formatINR } from "../data/catalog";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";

const STAGES = ["Order placed", "Packed", "Handed to courier", "Out for delivery", "Delivered"];

const stageOf = (order) => {
  const mins = (Date.now() - new Date(order.date).getTime()) / 60000;
  return Math.min(STAGES.length - 1, Math.floor(mins));
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SignIn = () => {
  const { completeSignIn, registerName } = useAuth();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [demoOtp, setDemoOtp] = useState(null);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const sendCode = async () => {
    if (busy) return;
    if (!EMAIL_RE.test(email.trim())) {
      setError("That doesn't look like a valid email — please check it.");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || "Could not send the code. Try again in a moment.");
      setStep("otp");
      setResendIn(30);
      setDemoOtp(data.demo_otp || null);
      toast.success("Code sent", { description: `Check the inbox for ${email.trim()}.` });
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (busy || !/^\d{6}$/.test(code.trim())) return;
    setError(null);
    setBusy(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), otp: code.trim() }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || "That code doesn't match. Check it and try again, or ask for a new one.");
      const res = completeSignIn(email.trim().toLowerCase());
      if (res.needsName) setStep("name");
      else toast.success("Signed in");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const resend = () => {
    setCode("");
    setDemoOtp(null);
    sendCode();
  };

  const saveName = () => {
    if (!name.trim()) return;
    registerName(email.trim().toLowerCase(), name.trim());
    toast.success("Signed in", { description: `Good to meet you, ${name.trim()}.` });
  };

  return (
    <div className="mx-auto max-w-md px-4 pb-28 pt-40 md:pt-48" data-testid="signin-screen">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
        <div className="border hairline bg-white/60 p-8 md:p-10">
          <p className="eyebrow">Your account</p>
          <h1 className="mt-4 font-serif text-3xl font-light">Sign in to your account.</h1>

          <AnimatePresence mode="wait">
            {step === "email" && (
              <motion.div key="email" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4, ease: EASE }}>
                <p className="mt-4 text-sm leading-relaxed text-ink/65">
                  Enter your email and we'll send you a code. First time here? Just enter your
                  email — we'll create your account as you go.
                </p>
                <label className="mt-8 block">
                  <span className="text-[11px] uppercase tracking-[0.22em] text-ink/55">Email</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(null); }}
                    placeholder="you@example.com"
                    data-testid="signin-email"
                    className="mt-2 w-full border hairline bg-cream px-4 py-3.5 text-[15px] placeholder:text-ink/30"
                  />
                </label>
                {error && <p className="mt-3 text-[13px] text-ink/60" data-testid="signin-error">{error}</p>}
                <button onClick={sendCode} disabled={busy} data-testid="send-code-button" className="mt-6 w-full rounded-full bg-ink px-8 py-4 text-[13px] uppercase tracking-[0.2em] text-cream transition-colors hover:bg-ink-deep disabled:opacity-60">
                  {busy ? "Sending…" : "Send Code"}
                </button>
                <p className="mt-4 text-[12px] leading-relaxed text-ink/45">
                  No password, ever. The code lands in your inbox and is valid for 10 minutes.
                </p>
              </motion.div>
            )}

            {step === "otp" && (
              <motion.div key="otp" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4, ease: EASE }}>
                <p className="mt-4 text-sm leading-relaxed text-ink/65">
                  Enter the code. We sent a 6-digit code to {email}.
                </p>
                <label className="mt-8 block">
                  <span className="text-[11px] uppercase tracking-[0.22em] text-ink/55">6-digit code</span>
                  <input
                    value={code}
                    onChange={(e) => { setCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(null); }}
                    placeholder="······"
                    inputMode="numeric"
                    data-testid="signin-otp"
                    className="mt-2 w-full border hairline bg-cream px-4 py-3.5 text-center font-serif text-2xl tracking-[0.5em] placeholder:text-ink/25"
                  />
                </label>
                {demoOtp && (
                  <p className="mt-3 text-[12px] text-ink/45" data-testid="demo-otp-hint">
                    Prototype mode — the code is {demoOtp}.
                  </p>
                )}
                {error && <p className="mt-3 text-[13px] text-ink/60" data-testid="otp-error">{error}</p>}
                <button onClick={verify} disabled={busy} data-testid="verify-code-button" className="mt-6 w-full rounded-full bg-ink px-8 py-4 text-[13px] uppercase tracking-[0.2em] text-cream transition-colors hover:bg-ink-deep disabled:opacity-60">
                  Verify & Sign In
                </button>
                <div className="mt-4 flex justify-between text-[12px] uppercase tracking-[0.16em] text-ink/50">
                  <button
                    onClick={resend}
                    disabled={resendIn > 0}
                    data-testid="resend-code"
                    className={`transition-colors ${resendIn > 0 ? "cursor-not-allowed text-ink/30" : "hover:text-ink"}`}
                  >
                    {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend"}
                  </button>
                  <button onClick={() => { setStep("email"); setCode(""); setError(null); }} data-testid="change-email" className="transition-colors hover:text-ink">Change email</button>
                </div>
                <p className="mt-4 text-[12px] leading-relaxed text-ink/45">
                  Didn't get it? Check the spam folder, or resend.
                </p>
              </motion.div>
            )}

            {step === "name" && (
              <motion.div key="name" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4, ease: EASE }}>
                <p className="mt-4 font-serif text-2xl font-light italic">What should we call you?</p>
                <label className="mt-8 block">
                  <span className="text-[11px] uppercase tracking-[0.22em] text-ink/55">Your name</span>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    data-testid="signin-name"
                    className="mt-2 w-full border hairline bg-cream px-4 py-3.5 text-[15px] placeholder:text-ink/30"
                  />
                </label>
                <button onClick={saveName} disabled={!name.trim()} data-testid="save-name-button" className={`mt-6 w-full rounded-full px-8 py-4 text-[13px] uppercase tracking-[0.2em] transition-colors ${name.trim() ? "bg-ink text-cream hover:bg-ink-deep" : "cursor-not-allowed bg-ink/15 text-ink/40"}`}>
                  Continue
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

const OrderCard = ({ order }) => {
  const [open, setOpen] = useState(false);
  const { add } = useCart();
  const stage = stageOf(order);

  return (
    <div className="border hairline bg-white/60" data-testid={`order-card-${order.id}`}>
      <button onClick={() => setOpen(!open)} className="flex w-full flex-wrap items-center justify-between gap-4 p-6 text-left" data-testid={`order-toggle-${order.id}`}>
        <div>
          <p className="font-serif text-lg">{order.id}</p>
          <p className="mt-1 text-[12px] text-ink/50">
            {new Date(order.date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })} · {order.items.reduce((a, i) => a + i.qty, 0)} items
          </p>
        </div>
        <div className="flex items-center gap-5">
          <span className="border border-sage-deep/40 px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-sage-deep" data-testid={`order-status-${order.id}`}>
            {STAGES[stage]}
          </span>
          <p className="font-medium tabular-nums">{formatINR(order.total)}</p>
          <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.3 }}>
            <ChevronDown className="h-4 w-4 text-ink/40" />
          </motion.span>
        </div>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.4, ease: EASE }} className="overflow-hidden">
            <div className="border-t hairline p-6">
              <div className="space-y-3">
                {order.items.map((i) => (
                  <div key={i.id} className="flex items-center gap-4 text-sm">
                    <div className="h-12 w-10 shrink-0 overflow-hidden bg-[#EFEAE0]">
                      {i.image && <img src={i.image} alt={i.name} className="h-full w-full object-cover" />}
                    </div>
                    <p className="flex-1 text-ink/75">{i.name} <span className="text-ink/40">× {i.qty}</span></p>
                    <p className="tabular-nums">{formatINR(i.price * i.qty)}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6" data-testid={`order-timeline-${order.id}`}>
                <p className="eyebrow">Track order</p>
                <div className="mt-4 space-y-0">
                  {STAGES.map((s, i) => (
                    <div key={s} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <span className={`h-3 w-3 rounded-full border-2 ${i <= stage ? "border-sage-deep bg-sage-deep" : "border-ink/20 bg-cream"}`} />
                        {i < STAGES.length - 1 && <span className={`h-6 w-px ${i < stage ? "bg-sage-deep" : "bg-ink/15"}`} />}
                      </div>
                      <p className={`-mt-0.5 text-[13px] ${i <= stage ? "text-ink" : "text-ink/40"}`}>{s}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-[12px] text-ink/50">Courier name and tracking number appear here once handed over.</p>
              </div>
              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => { order.items.forEach((i) => add(i.id, i.qty, true)); toast.success("Added to cart", { description: "Same order, again." }); }}
                  data-testid={`reorder-${order.id}`}
                  className="inline-flex items-center gap-2 rounded-full border border-ink/30 px-5 py-2.5 text-[11px] uppercase tracking-[0.16em] transition-colors hover:bg-ink hover:text-cream"
                >
                  <RefreshCw className="h-3.5 w-3.5" /> Reorder
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const AddressBook = () => {
  const { addresses, saveAddress, deleteAddress } = useAuth();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", mobile: "", pincode: "", flat: "", street: "", city: "", state: "", default: false });
  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };
  const valid = form.name.trim() && form.flat.trim() && form.city.trim() && /^\d{6}$/.test(form.pincode);

  return (
    <div data-testid="addresses-section">
      {addresses.length === 0 && !adding && (
        <div className="border hairline bg-white/60 p-10 text-center">
          <MapPin className="mx-auto h-6 w-6 text-sage-deep" strokeWidth={1.5} />
          <p className="mt-4 font-serif text-xl italic">No saved addresses.</p>
          <p className="mt-2 text-sm text-ink/55">Add one and checkout gets faster next time.</p>
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        {addresses.map((a) => (
          <div key={a.id} className="border hairline bg-white/60 p-6" data-testid={`address-card-${a.id}`}>
            {a.default && <p className="text-[10px] uppercase tracking-[0.2em] text-sage-deep">Default</p>}
            <p className="mt-2 font-medium">{a.name}</p>
            <p className="mt-1 text-[13px] leading-relaxed text-ink/60">
              {a.flat}, {a.street}{a.landmark ? `, ${a.landmark}` : ""}, {a.city}, {a.state} — {a.pincode}
            </p>
            <div className="mt-4 flex gap-4 text-[11px] uppercase tracking-[0.16em]">
              {!a.default && (
                <button onClick={() => saveAddress({ ...a, default: true })} data-testid={`address-default-${a.id}`} className="text-ink/55 transition-colors hover:text-ink">Set default</button>
              )}
              <button onClick={() => deleteAddress(a.id)} data-testid={`address-delete-${a.id}`} className="text-ink/55 transition-colors hover:text-ink">Delete</button>
            </div>
          </div>
        ))}
      </div>

      {adding ? (
        <div className="mt-4 border hairline bg-white/60 p-6" data-testid="address-form">
          <div className="grid gap-5 sm:grid-cols-2">
            {[["name", "Full name"], ["mobile", "Mobile"], ["pincode", "Pincode"], ["flat", "Flat, house, building"], ["street", "Street, area"], ["city", "City"], ["state", "State"]].map(([k, label]) => (
              <label key={k} className="block">
                <span className="text-[11px] uppercase tracking-[0.2em] text-ink/55">{label}</span>
                <input name={k} value={form[k]} onChange={onChange} data-testid={`address-input-${k}`} className="mt-2 w-full border hairline bg-cream px-4 py-3 text-[15px]" />
              </label>
            ))}
          </div>
          <label className="mt-4 flex items-center gap-3 text-sm text-ink/65">
            <input type="checkbox" name="default" checked={form.default} onChange={onChange} data-testid="address-input-default" className="h-4 w-4 accent-[#1B4A5F]" />
            Make this my default address
          </label>
          <div className="mt-5 flex gap-3">
            <button
              onClick={() => { if (!valid) return; saveAddress({ id: `addr-${Date.now()}`, ...form }); setAdding(false); setForm({ name: "", mobile: "", pincode: "", flat: "", street: "", city: "", state: "", default: false }); toast.success("Address saved"); }}
              disabled={!valid}
              data-testid="address-save"
              className={`rounded-full px-6 py-3 text-[11px] uppercase tracking-[0.16em] transition-colors ${valid ? "bg-ink text-cream hover:bg-ink-deep" : "cursor-not-allowed bg-ink/15 text-ink/40"}`}
            >
              Save address
            </button>
            <button onClick={() => setAdding(false)} data-testid="address-cancel" className="text-[11px] uppercase tracking-[0.16em] text-ink/55 hover:text-ink">Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setAdding(true)} data-testid="address-add" className="mt-4 rounded-full border border-ink/30 px-6 py-3 text-[11px] uppercase tracking-[0.16em] transition-colors hover:bg-ink hover:text-cream">
          Add an address
        </button>
      )}
    </div>
  );
};

const AccountPage = () => {
  const { user, orders, updateDetails, signOut } = useAuth();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "orders");
  const [details, setDetails] = useState({ name: user?.name || "", phone: user?.phone || "" });

  useEffect(() => {
    const t = params.get("tab");
    if (t) setTab(t);
  }, [params]);

  if (!user) return <SignIn />;

  const myOrders = orders.filter((o) => o.email === user.email || true);

  const TABS = [
    { id: "orders", label: "My Orders", icon: Package },
    { id: "addresses", label: "Saved Addresses", icon: MapPin },
    { id: "details", label: "My Details", icon: User },
  ];

  return (
    <div className="mx-auto max-w-5xl px-4 pb-24 pt-36 md:px-8 md:pt-44" data-testid="account-page">
      <Reveal>
        <p className="eyebrow">Your account</p>
        <h1 className="mt-4 font-serif text-4xl font-light md:text-5xl">
          Hello, <span className="italic text-sage-deep">{user.name}.</span>
        </h1>
        <p className="mt-2 text-sm text-ink/55">{user.email}</p>
      </Reveal>

      <div className="mt-10 flex flex-wrap gap-2 border-b hairline pb-5" data-testid="account-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`account-tab-${t.id}`}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[12px] uppercase tracking-[0.16em] transition-colors duration-300 ${tab === t.id ? "bg-ink text-cream" : "border hairline text-ink/60 hover:text-ink"}`}
          >
            <t.icon className="h-3.5 w-3.5" /> {t.label}
          </button>
        ))}
        <button onClick={signOut} data-testid="sign-out-button" className="ml-auto text-[12px] uppercase tracking-[0.16em] text-ink/50 transition-colors hover:text-ink">
          Sign out of this device
        </button>
      </div>

      <div className="mt-8">
        {tab === "orders" && (
          <div className="space-y-4" data-testid="orders-list">
            {myOrders.length === 0 ? (
              <div className="border hairline bg-white/60 p-12 text-center" data-testid="orders-empty">
                <Sprig className="mx-auto h-12 w-auto text-sage-deep" />
                <p className="mt-5 font-serif text-xl italic">No orders yet.</p>
                <p className="mt-2 text-sm text-ink/55">When you place one, it'll show up here.</p>
                <Link to="/shop" data-testid="orders-start-shopping" className="mt-6 inline-block rounded-full bg-ink px-7 py-3.5 text-[12px] uppercase tracking-[0.18em] text-cream transition-colors hover:bg-ink-deep">
                  Start Shopping
                </Link>
              </div>
            ) : (
              myOrders.map((o) => <OrderCard key={o.id} order={o} />)
            )}
          </div>
        )}

        {tab === "addresses" && <AddressBook />}

        {tab === "details" && (
          <div className="max-w-md border hairline bg-white/60 p-8" data-testid="details-section">
            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.22em] text-ink/55">Name</span>
              <input value={details.name} onChange={(e) => setDetails((d) => ({ ...d, name: e.target.value }))} data-testid="details-name" className="mt-2 w-full border hairline bg-cream px-4 py-3 text-[15px]" />
            </label>
            <label className="mt-5 block">
              <span className="text-[11px] uppercase tracking-[0.22em] text-ink/55">Email</span>
              <input value={user.email} disabled data-testid="details-email" className="mt-2 w-full border hairline bg-cream-2 px-4 py-3 text-[15px] text-ink/45" />
            </label>
            <p className="mt-2 text-[11px] text-ink/45">This is your sign-in email. Changing it asks for a code on the new address.</p>
            <label className="mt-5 block">
              <span className="text-[11px] uppercase tracking-[0.22em] text-ink/55">Phone <span className="text-ink/35 normal-case tracking-normal">(optional, for the courier)</span></span>
              <input value={details.phone} onChange={(e) => setDetails((d) => ({ ...d, phone: e.target.value }))} data-testid="details-phone" placeholder="10-digit mobile" className="mt-2 w-full border hairline bg-cream px-4 py-3 text-[15px]" />
            </label>
            <button
              onClick={() => { updateDetails(details); toast.success("Details saved"); }}
              data-testid="details-save"
              className="mt-6 rounded-full bg-ink px-7 py-3.5 text-[12px] uppercase tracking-[0.18em] text-cream transition-colors hover:bg-ink-deep"
            >
              Save details
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountPage;
